import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * Phase 2c — Beat card for the Stage-2 (beats_v2) schema. Shows ALL
 * grounded fields: theme chips, character arcs, confidence badge, cited
 * scenes (clickable → MasterBreakdown hash jump), needs_review tooltip.
 */
export default function BeatCardV2({ beat, stageColor, stageBg, stageBorder, projectId }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const [from, to] = beat.emotional_shift?.includes('→')
    ? beat.emotional_shift.split('→').map(s => s.trim())
    : [beat.emotional_shift, ''];

  const confLow = beat.confidence === 'low';
  const reviewReason = beat.needs_review_reason;

  const jumpToScene = (sceneId) => (e) => {
    e.stopPropagation();
    if (!sceneId || !projectId) return;
    // Beat → Scene jump: navigate to MasterBreakdown w/ hash that the
    // page will scroll-and-glow on mount (handler added separately).
    navigate(`/projects/${projectId}/master-breakdown#scene-${sceneId}`);
  };

  return (
    <div
      data-testid="beat-card"
      data-testid-id={`beat-card-${beat.id}`}
      className="rounded-xl border p-4 cursor-pointer transition-all duration-200 relative"
      style={{
        background: expanded ? stageBg : 'var(--cb-surface)',
        borderColor: expanded ? stageBorder : 'var(--cb-border)',
        boxShadow: expanded ? `0 0 20px ${stageColor}15` : 'none',
      }}
      onClick={() => setExpanded(e => !e)}
    >
      {/* Top row: beat_type + confidence */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wider leading-tight"
              style={{ color: stageColor }}>
          {beat.beat_type}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {confLow ? (
            <span
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(251,113,133,0.15)', color: '#fb7185' }}
              title={reviewReason || 'Needs review'}
              data-testid="beat-confidence-low"
            >
              <AlertTriangle className="h-3 w-3" /> review
            </span>
          ) : (
            <span
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}
              data-testid="beat-confidence-high"
            >
              <CheckCircle2 className="h-3 w-3" /> high
            </span>
          )}
        </div>
      </div>

      {/* Summary */}
      <p className="text-[13px] leading-snug mb-3" style={{ color: 'var(--cb-text)' }}>
        {beat.summary}
      </p>

      {/* Emotional shift pill */}
      {beat.emotional_shift && (
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{ background: `${stageColor}15`, color: stageColor }}>
            {from}
          </span>
          <span style={{ color: 'var(--cb-text-dim)' }}>→</span>
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--cb-text-sec)' }}>
            {to}
          </span>
        </div>
      )}

      {/* Theme tag chips */}
      {Array.isArray(beat.theme_tags) && beat.theme_tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {beat.theme_tags.map((t, i) => (
            <span
              key={i}
              data-testid="beat-theme-chip"
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: '#4ade8015', color: '#4ade80' }}
            >
              #{t}
            </span>
          ))}
        </div>
      )}

      {/* Page range + chevron */}
      <div className="flex items-center justify-between">
        {beat.page_range && (
          <span className="text-[10px] font-mono" style={{ color: 'var(--cb-text-dim)' }}>
            {beat.page_range}
          </span>
        )}
        <ChevronDown
          className="h-3.5 w-3.5 transition-transform duration-200 ml-auto"
          style={{ color: 'var(--cb-text-dim)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          strokeWidth={2}
        />
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-3" style={{ borderColor: 'var(--cb-border)' }}>
          {beat.significance && (
            <p className="text-[11px] italic leading-relaxed" style={{ color: 'var(--cb-text-sec)' }}>
              {beat.significance}
            </p>
          )}

          {/* Character arcs advanced */}
          {Array.isArray(beat.character_arcs_advanced) && beat.character_arcs_advanced.length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-wider font-bold mb-1"
                 style={{ color: 'var(--cb-text-dim)' }}>Character arcs advanced</p>
              <div className="space-y-1">
                {beat.character_arcs_advanced.map((c, i) => (
                  <div
                    key={i}
                    data-testid="character-arc-row"
                    className="text-[11px] flex items-center gap-1.5"
                    style={{ color: 'var(--cb-text)' }}
                  >
                    <span className="font-semibold">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cited scenes — clickable chips */}
          {Array.isArray(beat.cited_scene_ids) && beat.cited_scene_ids.length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-wider font-bold mb-1"
                 style={{ color: 'var(--cb-text-dim)' }}>Cited scenes</p>
              <div className="flex flex-wrap gap-1">
                {beat.cited_scene_ids.map((sid, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid="beat-cited-scene-chip"
                    onClick={jumpToScene(sid)}
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded hover:scale-105 transition-transform"
                    style={{ background: stageColor + '20', color: stageColor }}
                    title={`Jump to scene ${sid.slice(0, 8)}`}
                  >
                    {sid.slice(0, 8)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {confLow && reviewReason && (
            <div className="text-[10px] rounded p-2"
                 style={{ background: 'rgba(251,113,133,0.08)', color: '#fb7185' }}>
              <span className="font-bold">Needs review:</span> {reviewReason}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
